import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useReactTable, getCoreRowModel, getSortedRowModel, flexRender } from '@tanstack/react-table';
import { Toaster, toast } from 'react-hot-toast';
import { getInventory, searchInventory, createInventory, updateInventory, deleteInventory, getApiBaseUrl } from '../utils/api';
import { roundRupees } from '../utils/money';
import { formatModelCode } from '../utils/modelCategoryRanges';
import InventoryModal from './InventoryModal';
import InventoryToolbar from './InventoryToolbar';

// Smart Tooltip Component
const SmartTooltip = ({ children, content, className = "" }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0, placement: 'bottom' });
  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);

  const calculatePosition = useCallback(() => {
    if (!triggerRef.current || !tooltipRef.current || !isVisible) return;

    const trigger = triggerRef.current.getBoundingClientRect();
    const tooltip = tooltipRef.current.getBoundingClientRect();
    const viewport = {
      width: window.innerWidth,
      height: window.innerHeight,
      scrollX: window.scrollX,
      scrollY: window.scrollY
    };

    // Find the nearest scrollable container (table container)
    let container = triggerRef.current.parentElement;
    while (container && container !== document.body) {
      const style = window.getComputedStyle(container);
      if (style.overflow === 'auto' || style.overflow === 'scroll' ||
          style.overflowY === 'auto' || style.overflowY === 'scroll') {
        break;
      }
      container = container.parentElement;
    }

    // If no scrollable container found, use viewport
    const containerRect = container && container !== document.body
      ? container.getBoundingClientRect()
      : { top: 0, left: 0, bottom: viewport.height, right: viewport.width };

    const triggerCenter = {
      x: trigger.left + trigger.width / 2,
      y: trigger.top + trigger.height / 2
    };

    // Calculate available space within the container
    const space = {
      top: trigger.top - containerRect.top,
      bottom: containerRect.bottom - trigger.bottom,
      left: trigger.left - containerRect.left,
      right: containerRect.right - trigger.right
    };

    // Determine best placement
    let placement = 'bottom';
    let maxSpace = space.bottom;

    if (space.top > maxSpace) {
      placement = 'top';
      maxSpace = space.top;
    }
    if (space.left > maxSpace) {
      placement = 'left';
      maxSpace = space.left;
    }
    if (space.right > maxSpace) {
      placement = 'right';
      maxSpace = space.right;
    }

    // If preferred placement doesn't have enough space, try alternatives
    if (placement === 'bottom' && space.bottom < tooltip.height + 16) {
      if (space.top >= tooltip.height + 16) {
        placement = 'top';
      } else if (space.left >= tooltip.width + 16) {
        placement = 'left';
      } else if (space.right >= tooltip.width + 16) {
        placement = 'right';
      }
    }

    // Calculate position based on placement
    let top = 0;
    let left = 0;

    switch (placement) {
      case 'top':
        top = trigger.top - tooltip.height - 8;
        left = Math.max(containerRect.left + 10, Math.min(triggerCenter.x - tooltip.width / 2, containerRect.right - tooltip.width - 10));
        break;
      case 'bottom':
        top = trigger.bottom + 8;
        left = Math.max(containerRect.left + 10, Math.min(triggerCenter.x - tooltip.width / 2, containerRect.right - tooltip.width - 10));
        break;
      case 'left':
        top = Math.max(containerRect.top + 10, Math.min(triggerCenter.y - tooltip.height / 2, containerRect.bottom - tooltip.height - 10));
        left = trigger.left - tooltip.width - 8;
        break;
      case 'right':
        top = Math.max(containerRect.top + 10, Math.min(triggerCenter.y - tooltip.height / 2, containerRect.bottom - tooltip.height - 10));
        left = trigger.right + 8;
        break;
    }

    // Ensure tooltip stays within container bounds
    if (container && container !== document.body) {
      top = Math.max(containerRect.top + 5, Math.min(top, containerRect.bottom - tooltip.height - 5));
      left = Math.max(containerRect.left + 5, Math.min(left, containerRect.right - tooltip.width - 5));
    }

    // Final viewport boundary check - ensure tooltip is always visible in browser window
    const tooltipBottom = top + tooltip.height;
    const tooltipRight = left + tooltip.width;
    const viewportBottom = viewport.height;
    const viewportRight = viewport.width;

    // If tooltip would extend below viewport, force it above
    if (tooltipBottom > viewportBottom && top > tooltip.height + 10) {
      top = trigger.top - tooltip.height - 8;
      placement = 'top';
    }

    // If tooltip would extend above viewport, force it below
    if (top < 10 && trigger.bottom + tooltip.height + 8 < viewportBottom) {
      top = trigger.bottom + 8;
      placement = 'bottom';
    }

    // Ensure tooltip stays within horizontal viewport bounds
    if (left < 10) {
      left = 10;
    } else if (tooltipRight > viewportRight - 10) {
      left = viewportRight - tooltip.width - 10;
    }

    // Final safety check - keep tooltip in viewport
    top = Math.max(5, Math.min(top, viewportBottom - tooltip.height - 5));
    left = Math.max(5, Math.min(left, viewportRight - tooltip.width - 5));

    setPosition({ top: top + viewport.scrollY, left: left + viewport.scrollX, placement });
  }, [isVisible]);

  useEffect(() => {
    if (isVisible) {
      // Calculate position after tooltip is rendered
      const timer = setTimeout(calculatePosition, 0);
      return () => clearTimeout(timer);
    }
  }, [isVisible, calculatePosition]);

  useEffect(() => {
    const handleScroll = () => {
      if (isVisible) calculatePosition();
    };

    const handleResize = () => {
      if (isVisible) calculatePosition();
    };

    if (isVisible) {
      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleResize);
      return () => {
        window.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [isVisible, calculatePosition]);

  return (
    <>
      <div
        ref={triggerRef}
        className={`relative ${className}`}
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
      >
        {children}
      </div>

      {isVisible && (
        <div
          ref={tooltipRef}
          className="absolute z-50 pointer-events-none"
          style={{
            top: position.top,
            left: position.left,
            transform: 'translateZ(0)', // Force GPU acceleration
          }}
        >
          <div className="bg-white p-4 rounded-lg shadow-2xl border border-gray-200 max-w-sm">
            {content}
            {/* Arrow pointer */}
            <div
              className={`absolute w-0 h-0 border-8 border-transparent ${
                position.placement === 'top' ? 'border-t-white top-full left-1/2 transform -translate-x-1/2 -mt-2 border-t-gray-200' :
                position.placement === 'bottom' ? 'border-b-white bottom-full left-1/2 transform -translate-y-1/2 -mb-2 border-b-gray-200' :
                position.placement === 'left' ? 'border-l-white left-full top-1/2 transform -translate-y-1/2 -ml-2 border-l-gray-200' :
                'border-r-white right-full top-1/2 transform -translate-y-1/2 -mr-2 border-r-gray-200'
              }`}
              style={{
                filter: 'drop-shadow(0 1px 2px rgb(0 0 0 / 0.1))'
              }}
            />
          </div>
        </div>
      )}
    </>
  );
};

const InventoryPage = () => {
  const [inventory, setInventory] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [sorting, setSorting] = useState([]);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  // Fetch inventory data
  const fetchInventory = useCallback(async () => {
    try {
      setLoading(true);
      let data;
      if (searchQuery.trim()) {
        data = await searchInventory(searchQuery);
      } else {
        data = await getInventory();
      }
      setInventory(data);
    } catch (error) {
      toast.error(error.message || 'Failed to load inventory');
      console.error('Error fetching inventory:', error);
    } finally {
      setLoading(false);
    }
  }, [searchQuery]);

  // Fetch categories on component mount
  const fetchCategories = useCallback(async () => {
    try {
      const baseUrl = getApiBaseUrl();
      const response = await fetch(`${baseUrl}/api/inventory/public/categories`);
      if (!response.ok) throw new Error('Failed to fetch categories');
      const cats = await response.json();
      setCategories(cats);
    } catch (error) {
      console.error('Error fetching categories:', error);
      // Don't show error toast for categories as it's not critical
    }
  }, []);

  // Fetch data on component mount and when search query changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchInventory();
    }, 150); // Reduced debounce for better responsiveness

    return () => clearTimeout(timeoutId);
  }, [searchQuery, fetchInventory]);

  // Fetch categories on mount
  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // Define table columns
  const columns = useMemo(
    () => [
      {
        accessorKey: 'modelName',
        header: 'Model Name',
        cell: (info) => info.getValue(),
      },
      {
        accessorKey: 'modelNumber',
        header: 'Model Number',
        cell: (info) => formatModelCode(info.row.original.size, info.getValue()),
      },
      {
        accessorKey: 'photo',
        header: 'Photo',
        cell: (info) => {
          const photo = info.getValue();
          const item = info.row.original;
          return photo ? (
            <SmartTooltip
              content={
                <div>
                  <img
                    src={photo}
                    alt={`${item.modelName} enlarged view`}
                    className="w-auto h-auto object-contain rounded border bg-gray-50 mx-auto block"
                    style={{ maxWidth: '300px', maxHeight: '300px' }}
                  />
                  <div className="text-sm font-medium text-gray-800 mt-3 text-center">
                    {item.modelName}
                  </div>
                  <div className="text-xs text-gray-600 text-center">
                    Model {formatModelCode(item.size, item.modelNumber)}
                  </div>
                  <div className="text-xs text-gray-500 text-center mt-1">
                    Stock: {item.currentStock} | Sold: {item.sold}
                  </div>
                </div>
              }
            >
              <img
                src={photo}
                alt={`${item.modelName} thumbnail`}
                className="w-12 h-12 object-cover rounded border shadow-sm cursor-pointer transition-transform duration-200 hover:scale-110"
              />
            </SmartTooltip>
          ) : (
            <div className="w-12 h-12 border-2 border-dashed border-gray-300 rounded flex items-center justify-center bg-gray-50">
              <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
          );
        },
      },
      {
        accessorKey: 'size',
        header: 'Category',
        cell: (info) => {
          const size = info.getValue();
          return size ? size.charAt(0).toUpperCase() + size.slice(1) : '-';
        },
      },
      {
        accessorKey: 'retailOnly',
        header: 'Availability',
        cell: (info) => {
          const retailOnly = info.getValue();
          return retailOnly ? (
            <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-800">
              Retail only
            </span>
          ) : (
            <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
              Wholesale OK
            </span>
          );
        },
      },
      {
        accessorKey: 'retailPrice',
        header: 'Retail Price (₹)',
        cell: (info) => {
          const price = info.getValue();
          return `₹${roundRupees(price).toLocaleString('en-IN')}`;
        },
      },
      {
        accessorKey: 'wholesalePrice',
        header: 'Wholesale Price (₹)',
        cell: (info) => {
          const price = info.getValue();
          return `₹${roundRupees(price).toLocaleString('en-IN')}`;
        },
      },
      {
        accessorKey: 'initialStock',
        header: 'Initial Stock',
        cell: (info) => info.getValue(),
      },
      {
        accessorKey: 'sold',
        header: 'Sold',
        cell: (info) => info.getValue(),
      },
      {
        accessorKey: 'currentStock',
        header: 'Remaining',
        cell: (info) => {
          const stock = info.getValue();
          return stock;
        },
      },
      {
        id: 'depletionPercent',
        header: 'Depletion %',
        cell: (info) => {
          const item = info.row.original;
          const depletionPercent = item.initialStock > 0 ? (((item.initialStock - item.currentStock) / item.initialStock) * 100).toFixed(1) : 0;

          let bgColor = 'bg-green-100 text-green-800';
          let status = 'Healthy';

          if (depletionPercent >= 90) {
            bgColor = 'bg-red-100 text-red-800';
            status = 'Critical';
          } else if (depletionPercent >= 75) {
            bgColor = 'bg-orange-100 text-orange-800';
            status = 'High';
          } else if (depletionPercent >= 50) {
            bgColor = 'bg-yellow-100 text-yellow-800';
            status = 'Medium';
          }

          return (
            <SmartTooltip
              content={
                <div className="text-sm">
                  <div className="font-medium mb-2">Stock Health Analysis</div>
                  <div className="space-y-1">
                    <div>Initial Stock: {item.initialStock}</div>
                    <div>Current Stock: {item.currentStock}</div>
                    <div>Sold: {item.sold}</div>
                    <div className="border-t pt-1 mt-2">
                      <div className="font-medium">Depletion = (Initial - Current) ÷ Initial × 100</div>
                      <div>Status: <span className={`font-medium ${bgColor}`}>{status}</span></div>
                    </div>
                  </div>
                </div>
              }
            >
              <span className={`px-2 py-1 rounded-full text-xs font-medium cursor-help ${bgColor}`}>
                {depletionPercent}%
              </span>
            </SmartTooltip>
          );
        },
        sortingFn: (rowA, rowB) => {
          const a = rowA.original.initialStock > 0 ? (((rowA.original.initialStock - rowA.original.currentStock) / rowA.original.initialStock) * 100) : 0;
          const b = rowB.original.initialStock > 0 ? (((rowB.original.initialStock - rowB.original.currentStock) / rowB.original.initialStock) * 100) : 0;
          return a - b;
        },
      },
      {
        id: 'sellThroughRate',
        header: 'Sell-Through %',
        cell: (info) => {
          const item = info.row.original;
          const sellThroughRate = item.initialStock > 0 ? ((item.sold / item.initialStock) * 100).toFixed(1) : 0;

          let bgColor = 'bg-gray-100 text-gray-800';
          let performance = 'New';

          if (sellThroughRate >= 80) {
            bgColor = 'bg-green-100 text-green-800';
            performance = 'Excellent';
          } else if (sellThroughRate >= 60) {
            bgColor = 'bg-blue-100 text-blue-800';
            performance = 'Good';
          } else if (sellThroughRate >= 40) {
            bgColor = 'bg-yellow-100 text-yellow-800';
            performance = 'Average';
          } else if (sellThroughRate >= 20) {
            bgColor = 'bg-orange-100 text-orange-800';
            performance = 'Slow';
          } else if (sellThroughRate > 0) {
            bgColor = 'bg-red-100 text-red-800';
            performance = 'Poor';
          }

          return (
            <SmartTooltip
              content={
                <div className="text-sm">
                  <div className="font-medium mb-2">Sales Performance</div>
                  <div className="space-y-1">
                    <div>Sold: {item.sold}</div>
                    <div>Initial Stock: {item.initialStock}</div>
                    <div className="border-t pt-1 mt-2">
                      <div className="font-medium">Sell-Through = Sold ÷ Initial × 100</div>
                      <div>Performance: <span className={`font-medium ${bgColor}`}>{performance}</span></div>
                    </div>
                  </div>
                </div>
              }
            >
              <span className={`px-2 py-1 rounded-full text-xs font-medium cursor-help ${bgColor}`}>
                {sellThroughRate}%
              </span>
            </SmartTooltip>
          );
        },
        sortingFn: (rowA, rowB) => {
          const a = rowA.original.initialStock > 0 ? ((rowA.original.sold / rowA.original.initialStock) * 100) : 0;
          const b = rowB.original.initialStock > 0 ? ((rowB.original.sold / rowB.original.initialStock) * 100) : 0;
          return a - b;
        },
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: (info) => {
          const item = info.row.original;
          return (
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setSelectedItem(item);
                  setIsModalOpen(true);
                }}
                className="admin-btn admin-btn-neutral px-3 py-1 text-sm rounded"
              >
                Edit
              </button>
              <button
                onClick={() => handleDeleteClick(item)}
                disabled={deleteLoading}
                className="admin-btn admin-btn-danger px-3 py-1 text-sm rounded disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Delete
              </button>
            </div>
          );
        },
      },
    ],
    [deleteLoading]
  );

  // Filter inventory based on selected category
  const filteredInventory = useMemo(() => {
    if (!selectedCategory) {
      return inventory; // Show all items if no category selected
    }
    return inventory.filter(item => item.size === selectedCategory);
  }, [inventory, selectedCategory]);

  // Initialize table
  const table = useReactTable({
    data: filteredInventory,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: {
      sorting,
    },
  });

  // Handle save (create or update)
  const handleSave = async (id, data) => {
    try {
      let result;
      if (id) {
        // Update
        result = await updateInventory(id, data);
        const retail = roundRupees(data.retailPrice);
        const wholesale = roundRupees(data.wholesalePrice);
        toast.success(
          `Inventory updated — Retail ₹${retail.toLocaleString('en-IN')}, Wholesale ₹${wholesale.toLocaleString('en-IN')}`
        );
      } else {
        // Create
        result = await createInventory(data);
        const retail = roundRupees(data.retailPrice);
        const wholesale = roundRupees(data.wholesalePrice);
        toast.success(
          `Inventory created — Retail ₹${retail.toLocaleString('en-IN')}, Wholesale ₹${wholesale.toLocaleString('en-IN')}`
        );
      }
      // Modal closes itself after create + photo upload; refresh happens on close
      return result;
    } catch (error) {
      toast.error(error.message || 'Failed to save inventory item');
      throw error; // Re-throw to let modal handle it
    }
  };

  // Handle modal close
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedItem(null);
    fetchInventory();
  };

  // Handle delete button click
  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setShowDeleteConfirm(true);
  };

  // Handle delete confirmation with optimistic updates
  const handleDeleteConfirm = async () => {
    if (!itemToDelete || deleteLoading) return;

    setDeleteLoading(true);
    const deletedItem = itemToDelete;

    // Optimistic update - remove item from UI immediately
    setInventory(prev => prev.filter(item => item._id !== deletedItem._id));
    setShowDeleteConfirm(false);
    setItemToDelete(null);

    try {
      await deleteInventory(deletedItem._id);
      toast.success('Inventory item deleted successfully!');
    } catch (error) {
      // Revert optimistic update on error
      toast.error(error.message || 'Failed to delete inventory item');
      setInventory(prev => [...prev, deletedItem].sort((a, b) => a.modelName.localeCompare(b.modelName)));
    } finally {
      setDeleteLoading(false);
    }
  };

  // Handle delete cancel
  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
    setItemToDelete(null);
  };

  return (
    <div className="inventory-page p-6">
      <Toaster position="top-right" />

      {/* Page Header */}
      <div className="mb-6 shrink-0">
        <h1 className="text-4xl font-bold text-gray-800 mb-2">
          Inventory Management
        </h1>
        <p className="text-gray-600">Manage your Ganesh idol inventory</p>
      </div>

      <div className="shrink-0">
        <InventoryToolbar
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          categories={categories}
          selectedCategory={selectedCategory}
          onCategoryChange={setSelectedCategory}
          onAddNew={() => {
            setSelectedItem(null);
            setIsModalOpen(true);
          }}
        />
      </div>

      {/* Table */}
      <div className="flex flex-col flex-1 min-h-0 overflow-hidden rounded-lg bg-white shadow-md">
        {loading ? (
          <div className="flex flex-1 min-h-0 items-center justify-center p-8 text-center text-gray-600">
            Loading...
          </div>
        ) : filteredInventory.length === 0 ? (
          <div className="flex flex-1 min-h-0 items-center justify-center p-8 text-center text-gray-600">
            {searchQuery
              ? 'No items found matching your search.'
              : selectedCategory
                ? `No items found in the selected category.`
                : 'No inventory items found.'
            }
          </div>
        ) : (
          <div className="inventory-table-scroll">
            <table className="w-full min-w-max">
              <thead className="sticky top-0 z-10 bg-gray-50 shadow-sm">
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        className="sticky top-0 z-10 bg-gray-50 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        <div className="flex items-center gap-2">
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {header.column.getIsSorted() && (
                            <span>
                              {header.column.getIsSorted() === 'asc' ? '↑' : '↓'}
                            </span>
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {table.getRowModel().rows.map((row) => {
                  const item = row.original;
                  const isLowStock = item.currentStock <= 5;
                  return (
                    <tr
                      key={row.id}
                      className={isLowStock ? 'bg-red-50 hover:bg-red-100' : 'hover:bg-gray-50'}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <td
                          key={cell.id}
                          className={`px-6 py-4 whitespace-nowrap text-sm ${
                            isLowStock ? 'text-red-800 font-semibold' : 'text-gray-900'
                          }`}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Low Stock Warning */}
      {inventory.some((item) => item.currentStock <= 5) && (
        <div className="mt-4 shrink-0 rounded-lg border border-red-300 bg-red-100 p-4 text-red-800">
          <p className="font-semibold">Low Stock Alert</p>
          <p className="text-sm">
            Items with remaining stock of 5 or less are highlighted in red.
          </p>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && itemToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Delete Inventory Item</h3>
              <p className="text-sm text-gray-500 mb-4">
                Are you sure you want to delete <strong>"{itemToDelete.modelName}"</strong> (Model: {formatModelCode(itemToDelete.size, itemToDelete.modelNumber)})?
                This action cannot be undone.
              </p>
              <div className="flex justify-center gap-3">
                <button
                  onClick={handleDeleteCancel}
                  disabled={deleteLoading}
                  className="admin-btn admin-btn-modal-cancel disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  disabled={deleteLoading}
                  className="admin-btn admin-btn-modal-confirm admin-btn-danger disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {deleteLoading && (
                    <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                  )}
                  {deleteLoading ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal */}
      <InventoryModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSave}
        item={selectedItem}
        inventory={inventory}
      />
    </div>
  );
};

export default InventoryPage;
